import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const ORGANISMES = ['SAR', 'AMAIR', 'ADAR'];

function pct(accepted, concerned) {
  if (!concerned) return 0;
  return Math.round((accepted / concerned) * 100);
}

export default function SuperAdminCampagneDocuments() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [version, setVersion] = useState('2026-09');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const roles = user?.roles || [user?.role];
    if (!user || !roles.includes('super_admin')) {
      navigate('/');
      return;
    }
    fetchCampagne();
  }, [user, navigate]);

  const fetchCampagne = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data: payload } = await axios.get(
        `${process.env.REACT_APP_API_URL}/documents-statutaires/campagne`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(payload);
      setVersion(payload.version || '2026-09');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    const nextActive = !data?.active;
    const confirmMsg = nextActive
      ? 'Tous les adhérents concernés (y compris les administrateurs) seront bloqués jusqu’à acceptation des documents. Continuer ?'
      : 'Désactiver la campagne ? Les personnes n’ayant pas encore accepté pourront à nouveau utiliser la plateforme.';
    if (!window.confirm(confirmMsg)) return;

    setSaving(true);
    setError('');
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/documents-statutaires/campagne`,
        { active: nextActive, version },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
      await fetchCampagne();
      setMessage(
        nextActive
          ? 'Campagne activée. Les adhérents concernés devront accepter les documents.'
          : 'Campagne désactivée.'
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVersion = async () => {
    if (!version.trim()) return;
    if (
      data?.active &&
      version.trim() !== data.version &&
      !window.confirm(
        'Changer la version relancera l’acceptation pour toutes les personnes concernées. Continuer ?'
      )
    ) {
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/documents-statutaires/campagne`,
        { version: version.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
      await fetchCampagne();
      setMessage('Version enregistrée.');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const stats = data?.stats;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start gap-3 mb-8">
        <DocumentTextIcon className="h-8 w-8 text-indigo-600 flex-shrink-0" />
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Campagne d’acceptation des documents
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Statuts et règlements intérieurs SAR, AMAIR et ADAR. Une fois la
            campagne active, toute personne avec une adhésion active ou en
            attente doit re-cocher les documents de ses organismes avant
            d’accéder à la plateforme.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {message && (
        <div className="mb-4 rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
          {message}
        </div>
      )}

      {loading ? (
        <p className="text-slate-500">Chargement…</p>
      ) : (
        <>
          <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-slate-500">Statut</p>
                <p className="mt-1 text-lg font-semibold">
                  {data?.active ? (
                    <span className="text-green-700">Campagne active</span>
                  ) : (
                    <span className="text-slate-700">Campagne inactive</span>
                  )}
                </p>
                {data?.activatedAt && (
                  <p className="text-xs text-slate-500 mt-1">
                    Activée le{' '}
                    {new Date(data.activatedAt).toLocaleString('fr-FR')}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={handleToggle}
                disabled={saving}
                className={`px-5 py-2.5 rounded-lg font-medium text-white disabled:opacity-50 ${
                  data?.active
                    ? 'bg-slate-700 hover:bg-slate-800'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {saving
                  ? 'Enregistrement…'
                  : data?.active
                    ? 'Désactiver'
                    : 'Activer la campagne'}
              </button>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1">
                <label
                  htmlFor="version"
                  className="block text-sm font-medium text-slate-700"
                >
                  Version des documents
                </label>
                <input
                  id="version"
                  type="text"
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Incrémenter cette valeur (ex. 2026-09) pour relancer une
                  acceptation lors d’une prochaine révision.
                </p>
              </div>
              <button
                type="button"
                onClick={handleSaveVersion}
                disabled={saving || version.trim() === data?.version}
                className="px-4 py-2 rounded-md border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Enregistrer la version
              </button>
            </div>
          </section>

          {stats && (
            <>
              <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <p className="text-sm text-slate-500">Personnes concernées</p>
                  <p className="mt-1 text-3xl font-semibold text-slate-900">
                    {stats.concerned}
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <p className="text-sm text-slate-500">Ont tout accepté</p>
                  <p className="mt-1 text-3xl font-semibold text-green-700">
                    {stats.accepted}
                    <span className="ml-2 text-base font-medium text-slate-500">
                      ({pct(stats.accepted, stats.concerned)} %)
                    </span>
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <p className="text-sm text-slate-500">En attente</p>
                  <p className="mt-1 text-3xl font-semibold text-amber-700">
                    {stats.pending}
                  </p>
                </div>
              </section>

              <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">
                  Par organisme
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500 border-b">
                        <th className="py-2 pr-4">Organisme</th>
                        <th className="py-2 pr-4">Concernés</th>
                        <th className="py-2 pr-4">Accepté</th>
                        <th className="py-2">Taux</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ORGANISMES.map((org) => {
                        const row = stats.byOrganisme?.[org] || {
                          concerned: 0,
                          accepted: 0,
                        };
                        return (
                          <tr key={org} className="border-b last:border-0">
                            <td className="py-2 pr-4 font-medium">{org}</td>
                            <td className="py-2 pr-4">{row.concerned}</td>
                            <td className="py-2 pr-4">{row.accepted}</td>
                            <td className="py-2">
                              {pct(row.accepted, row.concerned)} %
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">
                  Par document
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-500 border-b">
                        <th className="py-2 pr-4">Document</th>
                        <th className="py-2 pr-4">Organisme</th>
                        <th className="py-2 pr-4">Concernés</th>
                        <th className="py-2 pr-4">Accepté</th>
                        <th className="py-2">Taux</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(stats.byDocument || {}).map((doc) => (
                        <tr key={doc.key} className="border-b last:border-0">
                          <td className="py-2 pr-4">{doc.label}</td>
                          <td className="py-2 pr-4">{doc.organisme}</td>
                          <td className="py-2 pr-4">{doc.concerned}</td>
                          <td className="py-2 pr-4">{doc.accepted}</td>
                          <td className="py-2">
                            {pct(doc.accepted, doc.concerned)} %
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-1">
                  Personnes n’ayant pas encore accepté
                </h2>
                <p className="text-xs text-slate-500 mb-4">
                  {stats.pendingUsersTotal > (stats.pendingUsers || []).length
                    ? `Affichage des 100 premiers sur ${stats.pendingUsersTotal}.`
                    : `${stats.pendingUsersTotal || 0} personne(s).`}
                </p>
                {(stats.pendingUsers || []).length === 0 ? (
                  <div className="flex items-center gap-2 text-green-700 text-sm">
                    <CheckCircleIcon className="h-5 w-5" />
                    Tout le monde a accepté, ou la campagne n’a encore
                    personne de concerné.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-slate-500 border-b">
                          <th className="py-2 pr-4">Nom</th>
                          <th className="py-2 pr-4">Email</th>
                          <th className="py-2 pr-4">Organismes</th>
                          <th className="py-2">Documents manquants</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.pendingUsers.map((u) => (
                          <tr key={u._id} className="border-b last:border-0">
                            <td className="py-2 pr-4">
                              {u.prenom} {u.nom}
                            </td>
                            <td className="py-2 pr-4">{u.email}</td>
                            <td className="py-2 pr-4">
                              {(u.organismes || []).join(', ')}
                            </td>
                            <td className="py-2">
                              {(u.missingDocuments || [])
                                .map((d) => d.label)
                                .join(', ')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}

          {!data?.active && (
            <div className="mt-6 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
              <p>
                Tant que la campagne n’est pas activée, personne n’est bloqué.
                Vérifiez que les PDF sont à jour dans{' '}
                <code className="text-xs">/documents/</code> avant d’activer.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
